import React from "react";
import {
  Paper,
  Box,
  Typography,
  useTheme,
  alpha,
  Chip,
  Avatar,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import PersonIcon from "@mui/icons-material/Person";
import PublicIcon from "@mui/icons-material/Public";

const TopContributorsChart = ({ data = [], loading = false }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isLight = theme.palette.mode === "light";

  // Enhanced glassmorphism background matching StatsCard
  const glassBg = isLight ? "rgba(255,255,255,0.90)" : "rgba(34,45,59,0.85)";

  // Dynamic shadow matching StatsCard
  const boxShadow = isLight
    ? "0 4px 24px 0 rgba(2,89,165,0.10), 0 1.5px 8px 0 rgba(0,0,0,0.03)"
    : "0 4px 24px 0 rgba(0,188,212,0.07), 0 1.5px 8px 0 rgba(0,0,0,0.25)";

  // Border color matching StatsCard
  const borderColor = isLight ? "rgba(2,89,165,0.08)" : "rgba(0,188,212,0.18)";

  // Color palette for bars - different colors for each user type
  const getBarColor = (type, index) => {
    let baseColor;
    switch (type?.toLowerCase()) {
      case "journalist":
        baseColor = theme.palette.success.main; // Green
        break;
      case "informant":
        baseColor = theme.palette.info.main; // Blue
        break;
      case "user":
        baseColor = "#9c27b0"; // Purple
        break;
      case "anonymous":
      default:
        baseColor = theme.palette.warning.main; // Orange
        break;
    }
    return alpha(baseColor, 1 - index * 0.07);
  };

  // Get color for specific type
  const getTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case "journalist":
        return theme.palette.success.main;
      case "informant":
        return theme.palette.info.main;
      case "user":
        return "#9c27b0";
      case "anonymous":
      default:
        return theme.palette.warning.main;
    }
  };

  // Get icon for user type
  const getTypeIcon = (type) => {
    switch (type?.toLowerCase()) {
      case "journalist":
        return <PersonIcon fontSize="small" />;
      case "informant":
        return <PersonIcon fontSize="small" />;
      case "user":
        return <PersonIcon fontSize="small" />;
      case "anonymous":
      default:
        return <PublicIcon fontSize="small" />;
    }
  };

  // Get display label for user type
  const getTypeLabel = (type) => {
    if (!type) return t("dashboard.userTypes.anonymous");
    const typeStr = type.toLowerCase();
    return t(`dashboard.userTypes.${typeStr}`);
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const typeColor = getTypeColor(data.type);

      return (
        <Paper
          elevation={3}
          sx={{
            p: 1.5,
            background: isLight
              ? "rgba(255,255,255,0.98)"
              : "rgba(34,45,59,0.98)",
            backdropFilter: "blur(10px)",
            border: `1px solid ${borderColor}`,
            borderRadius: 2,
            minWidth: 200,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: alpha(typeColor, 0.2),
                color: typeColor,
              }}
            >
              {getTypeIcon(data.type)}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: "0.8rem", md: "0.875rem" },
                  color: theme.palette.text.primary,
                }}
              >
                {data.label}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontSize: { xs: "0.7rem", md: "0.75rem" },
                  color: theme.palette.text.secondary,
                  display: "block",
                }}
              >
                {data.email}
              </Typography>
            </Box>
          </Box>
          <Chip
            label={getTypeLabel(data.type)}
            size="small"
            sx={{
              bgcolor: alpha(typeColor, 0.15),
              color: typeColor,
              fontWeight: 600,
              fontSize: "0.7rem",
              height: 20,
              mb: 0.5,
            }}
          />
          <Typography
            variant="body2"
            sx={{
              color: typeColor,
              fontWeight: 600,
              mt: 0.5,
            }}
          >
            {data.value}{" "}
            {data.value !== 1
              ? t("dashboard.contributionPlural")
              : t("dashboard.contribution")}
          </Typography>
        </Paper>
      );
    }
    return null;
  };

  // Format chart data - top 5 only, show first 2 words of name
  const chartData = data.slice(0, 5).map((item) => {
    const words = item.label.split(" ");
    const displayLabel =
      words.length > 2 ? words.slice(0, 2).join(" ") + "..." : item.label;

    return {
      ...item,
      displayLabel,
    };
  });

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 2.5, md: 3 },
        background: glassBg,
        backdropFilter: "blur(16px)",
        WebkitBackdropFilter: "blur(16px)",
        border: `1px solid ${borderColor}`,
        borderRadius: { xs: 2.5, md: 3 },
        boxShadow,
        height: "100%",
        minHeight: { xs: 400, sm: 450, md: 500 },
        display: "flex",
        flexDirection: "column",
        position: "relative",
        overflow: "hidden",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
          boxShadow: isLight
            ? "0 8px 40px 0 rgba(2,89,165,0.15), 0 3px 16px 0 rgba(0,0,0,0.06)"
            : "0 8px 40px 0 rgba(0,188,212,0.12), 0 3px 16px 0 rgba(0,0,0,0.30)",
          transform: "translateY(-2px)",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          mb: { xs: 2, md: 3 },
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 1.5, sm: 0 },
          minHeight: 64,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 1, md: 1.5 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: { xs: 40, md: 48 },
              height: { xs: 40, md: 48 },
              borderRadius: 2,
              background: `linear-gradient(135deg, ${alpha(
                theme.palette.success.main,
                0.2,
              )} 0%, ${alpha(theme.palette.warning.main, 0.2)} 100%)`,
              border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
            }}
          >
            <EmojiEventsIcon
              sx={{
                fontSize: { xs: 24, md: 28 },
                color: theme.palette.success.main,
              }}
            />
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: theme.palette.text.primary,
                fontSize: { xs: "1rem", sm: "1.05rem", md: "1.1rem" },
                letterSpacing: "-0.01em",
              }}
            >
              {t("dashboard.topContributors")}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: { xs: "0.7rem", md: "0.75rem" },
              }}
            >
              {t("dashboard.topContributors")}
            </Typography>
          </Box>
        </Box>
        <Chip
          label={`Top ${Math.min(data.length, 5)}`}
          size="small"
          sx={{
            bgcolor: alpha(theme.palette.success.main, 0.12),
            color: theme.palette.success.main,
            fontWeight: 600,
            fontSize: { xs: "0.7rem", md: "0.75rem" },
            height: { xs: 24, md: 26 },
            "& .MuiChip-label": {
              px: { xs: 1, md: 1.5 },
            },
          }}
        />
      </Box>

      {/* Legend */}
      <Box
        sx={{
          display: "flex",
          gap: { xs: 1.5, sm: 2 },
          mb: 2,
          justifyContent: "center",
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box
            sx={{
              width: { xs: 10, md: 12 },
              height: { xs: 10, md: 12 },
              borderRadius: 0.5,
              bgcolor: theme.palette.success.main,
            }}
          />
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontSize: { xs: "0.65rem", md: "0.7rem" },
            }}
          >
            Journalist
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box
            sx={{
              width: { xs: 10, md: 12 },
              height: { xs: 10, md: 12 },
              borderRadius: 0.5,
              bgcolor: theme.palette.info.main,
            }}
          />
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontSize: { xs: "0.65rem", md: "0.7rem" },
            }}
          >
            Informant
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box
            sx={{
              width: { xs: 10, md: 12 },
              height: { xs: 10, md: 12 },
              borderRadius: 0.5,
              bgcolor: "#9c27b0",
            }}
          />
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontSize: { xs: "0.65rem", md: "0.7rem" },
            }}
          >
            User
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box
            sx={{
              width: { xs: 10, md: 12 },
              height: { xs: 10, md: 12 },
              borderRadius: 0.5,
              bgcolor: theme.palette.warning.main,
            }}
          />
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontSize: { xs: "0.65rem", md: "0.7rem" },
            }}
          >
            Anonymous
          </Typography>
        </Box>
      </Box>

      {/* Chart */}
      <Box
        sx={{
          width: "100%",
          height: { xs: 250, sm: 280, md: 320 },
          flex: 1,
          display: "flex",
          alignItems: "center",
        }}
      >
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: { xs: "0.8rem", md: "0.875rem" },
              }}
            >
              Loading...
            </Typography>
          </Box>
        ) : data.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <EmojiEventsIcon
              sx={{
                fontSize: 48,
                color: theme.palette.text.disabled,
                opacity: 0.3,
              }}
            />
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: { xs: "0.8rem", md: "0.875rem" },
                textAlign: "center",
              }}
            >
              No contributor data available
            </Typography>
          </Box>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{
                top: 5,
                right: 10,
                left: -20,
                bottom: 5,
              }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke={alpha(theme.palette.divider, 0.3)}
                vertical={false}
              />
              <XAxis
                dataKey="displayLabel"
                tick={{
                  fill: theme.palette.text.secondary,
                  fontSize: 10,
                }}
                stroke={theme.palette.divider}
                tickLine={false}
                axisLine={{ stroke: alpha(theme.palette.divider, 0.5) }}
              />
              <YAxis
                tick={{
                  fill: theme.palette.text.secondary,
                  fontSize: 10,
                }}
                stroke={theme.palette.divider}
                tickLine={false}
                axisLine={{ stroke: alpha(theme.palette.divider, 0.5) }}
                allowDecimals={false}
              />
              <RechartsTooltip content={<CustomTooltip />} cursor={false} />
              <Bar
                dataKey="value"
                radius={[8, 8, 0, 0]}
                maxBarSize={60}
                animationDuration={800}
                animationEasing="ease-out"
              >
                {chartData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={getBarColor(entry.type, index)}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </Box>
    </Paper>
  );
};

export default TopContributorsChart;
